<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import nexceoProjectsAPI from 'dashboard/api/nexceo/projects';
import sourceRulesAPI from 'dashboard/api/nexceo/sourceRules';
import teamMembersAPI from 'dashboard/api/nexceo/projectTeamMembers';
import staffAiConversationsAPI from 'dashboard/api/nexceo/staffAiConversations';
import agentsAPI from 'dashboard/api/agents';
import inboxesAPI from 'dashboard/api/inboxes';
import ProjectDocuments from './ProjectDocuments.vue';
import ProjectInventory from './ProjectInventory.vue';
import ProjectWorkspaceLayout from './ProjectWorkspaceLayout.vue';
import { projectWorkspaceTabs } from './projectWorkspaceTabs';

const route = useRoute();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();

const isEditing = computed(() => !!route.params.id);
const workspaceTabs = computed(() =>
  projectWorkspaceTabs({
    standalone: false,
    id: route.params.id,
    accountId: accountId.value,
  })
);
const activeTab = ref('details');
const isLoading = ref(false);
const isSaving = ref(false);
const error = ref(null);
const successMsg = ref('');

// ── Source rules ──────────────────────────────────────────────────────────────
const sourceRules = ref([]);
const rulesLoading = ref(false);
const showRuleForm = ref(false);
const ruleForm = reactive({
  inbox_id: '',
  channel_type: '',
  source_name: '',
  match_mode: 'hint',
  priority: 50,
  active: true,
});
const ruleSaving = ref(false);

const CHANNEL_TYPES = [
  { value: '', label: 'Any' },
  { value: 'Channel::Api', label: 'API' },
  { value: 'Channel::Email', label: 'Email' },
  { value: 'Channel::FacebookPage', label: 'Facebook' },
  { value: 'Channel::Instagram', label: 'Instagram' },
  { value: 'Channel::Line', label: 'Line' },
  { value: 'Channel::Sms', label: 'SMS' },
  { value: 'Channel::Telegram', label: 'Telegram' },
  { value: 'Channel::TwitterProfile', label: 'Twitter/X' },
  { value: 'Channel::WebWidget', label: 'Website Chat' },
  { value: 'Channel::Whatsapp', label: 'WhatsApp' },
];
const MATCH_MODES = [
  { value: 'hint', label: 'Hint — weak signal' },
  { value: 'strong_hint', label: 'Strong hint — prioritised' },
  { value: 'default_for_inbox', label: 'Default for inbox — confirmed match' },
];

const accountInboxes = ref([]);

async function loadSourceRules() {
  if (!isEditing.value) return;
  rulesLoading.value = true;
  try {
    const { data } = await sourceRulesAPI.list(route.params.id);
    sourceRules.value = data.source_rules ?? [];
  } catch {
    // non-fatal
  } finally {
    rulesLoading.value = false;
  }
}

async function loadInboxes() {
  try {
    const { data } = await inboxesAPI.get();
    accountInboxes.value = data.payload ?? [];
  } catch {
    accountInboxes.value = [];
  }
}

async function saveRule() {
  ruleSaving.value = true;
  try {
    const payload = {
      inbox_id: ruleForm.inbox_id || null,
      channel_type: ruleForm.channel_type || null,
      source_name: ruleForm.source_name || null,
      match_mode: ruleForm.match_mode,
      priority: Number(ruleForm.priority) || 50,
      active: ruleForm.active,
    };
    await sourceRulesAPI.create(route.params.id, payload);
    showRuleForm.value = false;
    Object.assign(ruleForm, {
      inbox_id: '',
      channel_type: '',
      source_name: '',
      match_mode: 'hint',
      priority: 50,
      active: true,
    });
    await loadSourceRules();
  } catch (e) {
    error.value =
      e?.response?.data?.errors?.join(', ') ?? 'Failed to save rule.';
  } finally {
    ruleSaving.value = false;
  }
}

async function deleteRule(ruleId) {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!window.confirm('Delete this source rule?')) return;
  try {
    await sourceRulesAPI.destroy(route.params.id, ruleId);
    await loadSourceRules();
  } catch {
    // non-fatal
  }
}

// ── Team Members ──────────────────────────────────────────────────────────────
const teamMembers = ref([]);
const membersLoading = ref(false);
const showMemberForm = ref(false);
const memberForm = reactive({
  user_id: '',
  role: 'member',
  notify: true,
  name: '',
  email: '',
  whatsapp_number: '',
  phone_number: '',
  department: '',
  preferred_notification_channel: 'private_note',
  responsibilities: [],
});
const memberSaving = ref(false);
const accountAgents = ref([]);

const editingMemberId = ref(null);
const editForm = reactive({
  role: 'member',
  notify: true,
  name: '',
  email: '',
  whatsapp_number: '',
  phone_number: '',
  department: '',
  preferred_notification_channel: 'private_note',
  responsibilities: [],
  active: true,
});
const editSaving = ref(false);

// Only two roles shown in UI going forward.
const TEAM_ROLES = [
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'project_executive', label: 'Project Executive' },
];

// M26.11 Part 1/13 — functional contacts, independent of the two roles
// above. Drives notification routing only, never access control. Keep in
// sync with Nexceo::ProjectTeamMember::RESPONSIBILITY_LABELS.
const RESPONSIBILITIES = [
  { value: 'sales', label: 'Primary Sales Person' },
  { value: 'relationship_manager', label: 'Relationship Manager' },
  { value: 'site_visit', label: 'Site Visit Manager' },
  { value: 'finance', label: 'Finance Contact' },
  { value: 'documentation', label: 'Documentation Contact' },
  { value: 'project_head', label: 'Project Head' },
];

const NOTIFICATION_CHANNELS = [
  { value: 'private_note', label: 'Dashboard (private note)' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'call', label: 'Call' },
];

// Maps any legacy DB role → display label for existing records.
const LEGACY_ROLE_LABELS = {
  lead_manager: 'Project Manager',
  project_manager: 'Project Manager',
  site_visit_coordinator: 'Project Executive',
  notification_recipient: 'Project Executive',
  member: 'Project Executive',
  project_executive: 'Project Executive',
};

function displayRoleLabel(role) {
  return (
    LEGACY_ROLE_LABELS[role] ||
    TEAM_ROLES.find(r => r.value === role)?.label ||
    role
  );
}

function onAgentSelected() {
  const agent = accountAgents.value.find(
    a => String(a.id) === String(memberForm.user_id)
  );
  if (!agent) return;
  if (!memberForm.name) memberForm.name = agent.name ?? '';
  if (!memberForm.email) memberForm.email = agent.email ?? '';
}

async function loadTeamMembers() {
  if (!isEditing.value) return;
  membersLoading.value = true;
  try {
    const { data } = await teamMembersAPI.list(route.params.id);
    teamMembers.value = data.team_members ?? [];
  } catch {
    // non-fatal
  } finally {
    membersLoading.value = false;
  }
}

async function loadAgents() {
  try {
    const { data } = await agentsAPI.get();
    accountAgents.value = data ?? [];
  } catch {
    accountAgents.value = [];
  }
}

async function saveMember() {
  if (!memberForm.user_id) return;
  memberSaving.value = true;
  try {
    await teamMembersAPI.create(route.params.id, {
      user_id: Number(memberForm.user_id),
      role: memberForm.role,
      notify: memberForm.notify,
      name: memberForm.name.trim() || null,
      email: memberForm.email.trim() || null,
      whatsapp_number: memberForm.whatsapp_number.trim() || null,
      phone_number: memberForm.phone_number.trim() || null,
      department: memberForm.department.trim() || null,
      preferred_notification_channel: memberForm.preferred_notification_channel,
      responsibilities: memberForm.responsibilities,
    });
    showMemberForm.value = false;
    Object.assign(memberForm, {
      user_id: '',
      role: 'member',
      notify: true,
      name: '',
      email: '',
      whatsapp_number: '',
      phone_number: '',
      department: '',
      preferred_notification_channel: 'private_note',
      responsibilities: [],
    });
    await loadTeamMembers();
  } catch (e) {
    error.value =
      e?.response?.data?.errors?.join(', ') ?? 'Failed to add team member.';
  } finally {
    memberSaving.value = false;
  }
}

function startEditMember(member) {
  editingMemberId.value = member.id;
  Object.assign(editForm, {
    role: member.role,
    notify: member.notify,
    name: member.name ?? '',
    email: member.email ?? '',
    whatsapp_number: member.whatsapp_number ?? '',
    phone_number: member.phone_number ?? '',
    department: member.department ?? '',
    preferred_notification_channel:
      member.preferred_notification_channel ?? 'private_note',
    responsibilities: [...(member.responsibilities ?? [])],
    active: member.active !== false,
  });
}

function cancelEditMember() {
  editingMemberId.value = null;
}

async function updateMember(memberId) {
  editSaving.value = true;
  try {
    await teamMembersAPI.update(route.params.id, memberId, {
      role: editForm.role,
      notify: editForm.notify,
      name: editForm.name.trim() || null,
      email: editForm.email.trim() || null,
      whatsapp_number: editForm.whatsapp_number.trim() || null,
      phone_number: editForm.phone_number.trim() || null,
      department: editForm.department.trim() || null,
      preferred_notification_channel: editForm.preferred_notification_channel,
      responsibilities: editForm.responsibilities,
      active: editForm.active,
    });
    editingMemberId.value = null;
    await loadTeamMembers();
  } catch (e) {
    error.value =
      e?.response?.data?.errors?.join(', ') ?? 'Failed to update team member.';
  } finally {
    editSaving.value = false;
  }
}

async function deleteMember(memberId) {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (!window.confirm('Remove this team member?')) return;
  // Optimistically remove from list so UI responds immediately
  const prev = teamMembers.value;
  teamMembers.value = teamMembers.value.filter(m => m.id !== memberId);
  try {
    await teamMembersAPI.destroy(route.params.id, memberId);
  } catch (err) {
    // Restore list and tell user
    teamMembers.value = prev;
    const msg =
      err?.response?.data?.error ||
      'Could not remove team member. Please try again.';
    // eslint-disable-next-line no-restricted-globals, no-alert
    window.alert(msg);
  }
}

// ── Staff AI setup per team member ─────────────────────────────────────────
const staffAiSetupLoading = ref(null); // holds member.id being set up
const staffAiSetupResult = ref({}); // { [memberId]: { success, message } }

async function setupStaffAiForMember(member) {
  staffAiSetupLoading.value = member.id;
  staffAiSetupResult.value = { ...staffAiSetupResult.value, [member.id]: null };
  try {
    await staffAiConversationsAPI.setupForMember(member.id);
    staffAiSetupResult.value = {
      ...staffAiSetupResult.value,
      [member.id]: { success: true, message: 'Staff AI chat created.' },
    };
    await loadTeamMembers(); // refresh to get updated staff_ai_conversation status
  } catch (e) {
    const msg =
      e?.response?.data?.message || e?.response?.data?.error || 'Setup failed.';
    staffAiSetupResult.value = {
      ...staffAiSetupResult.value,
      [member.id]: { success: false, message: msg },
    };
  } finally {
    staffAiSetupLoading.value = null;
  }
}

const form = reactive({
  name: '',
  description: '',
  location: '',
  city: '',
  state: '',
  country: 'India',
  property_type: '',
  price_min: '',
  price_max: '',
  currency: 'INR',
  status: 'under_construction',
  delivery_year: null,
  ai_enabled: true,
  location_url: '',
  rera_number: '',
  latitude: '',
  longitude: '',
});

const bedroomInput = ref('');
const bedrooms = ref([]);
const amenityInput = ref('');
const amenities = ref([]);

const PROPERTY_TYPES = [
  'Apartment',
  'Villa',
  'Plot',
  'Commercial',
  'Penthouse',
  'Row House',
  'Studio',
];
const STATUSES = ['under_construction', 'ready_to_move', 'sold'];
const STATUS_LABELS = {
  under_construction: 'Under Construction',
  ready_to_move: 'Ready to Move',
  sold: 'Sold',
};
const currentYear = new Date().getFullYear();
const DELIVERY_YEAR_OPTIONS = Array.from(
  { length: 16 },
  (_, i) => currentYear + i
);

const COMMON_BEDROOMS = [
  '1RK',
  '1BHK',
  '2BHK',
  '3BHK',
  '4BHK',
  '5BHK',
  'Penthouse',
];
const COMMON_AMENITIES = [
  'Swimming Pool',
  'Gym',
  'Club House',
  'Parking',
  'Power Backup',
  'Security',
  'Garden',
  'Kids Play Area',
];

async function loadProject() {
  if (!isEditing.value) return;
  isLoading.value = true;
  try {
    const { data } = await nexceoProjectsAPI.show(route.params.id);
    Object.assign(form, {
      name: data.name ?? '',
      description: data.description ?? '',
      location: data.location ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      country: data.country ?? 'India',
      property_type: data.property_type ?? '',
      ai_enabled: data.ai_enabled ?? true,
      price_min: data.price_min ?? '',
      price_max: data.price_max ?? '',
      currency: data.currency ?? 'INR',
      status: data.status ?? 'under_construction',
      delivery_year: data.delivery_year ?? null,
      location_url: data.location_url ?? '',
      rera_number: data.rera_number ?? '',
      latitude: data.latitude ?? '',
      longitude: data.longitude ?? '',
    });
    bedrooms.value = data.bedroom_options ?? [];
    amenities.value = data.amenities ?? [];
  } catch {
    error.value = 'Failed to load project.';
  } finally {
    isLoading.value = false;
  }
}

// Best-effort convenience only — many pasted Google Maps share links embed
// the coordinates in one of a few known URL shapes (@lat,lng,zoom or
// !3dlat!4dlng or ?q=lat,lng). Falls back to asking the admin to type them
// in when a link doesn't match, rather than guessing.
const coordExtractError = ref('');
function extractCoordsFromUrl() {
  coordExtractError.value = '';
  const url = form.location_url || '';
  const patterns = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
    /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/,
  ];
  const match = patterns.map(p => url.match(p)).find(Boolean);
  if (!match) {
    coordExtractError.value =
      "Couldn't find coordinates in that link — enter latitude/longitude manually.";
    return;
  }
  form.latitude = match[1];
  form.longitude = match[2];
}

function addBedroom() {
  const val = bedroomInput.value.trim();
  if (val && !bedrooms.value.includes(val)) bedrooms.value.push(val);
  bedroomInput.value = '';
}
function toggleBedroom(b) {
  const idx = bedrooms.value.indexOf(b);
  if (idx === -1) bedrooms.value.push(b);
  else bedrooms.value.splice(idx, 1);
}
function removeBedroom(idx) {
  bedrooms.value.splice(idx, 1);
}

function addAmenity() {
  const val = amenityInput.value.trim();
  if (val && !amenities.value.includes(val)) amenities.value.push(val);
  amenityInput.value = '';
}
function toggleAmenity(a) {
  const idx = amenities.value.indexOf(a);
  if (idx === -1) amenities.value.push(a);
  else amenities.value.splice(idx, 1);
}
function removeAmenity(idx) {
  amenities.value.splice(idx, 1);
}

async function submit() {
  if (!form.name.trim()) {
    error.value = 'Project name is required.';
    return;
  }
  isSaving.value = true;
  error.value = null;
  successMsg.value = '';
  try {
    const payload = {
      project: {
        ...form,
        bedroom_options: bedrooms.value,
        amenities: amenities.value,
        price_min: form.price_min || null,
        price_max: form.price_max || null,
        delivery_year: form.delivery_year || null,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
      },
    };
    if (isEditing.value) {
      await nexceoProjectsAPI.update(route.params.id, payload);
      successMsg.value = 'Project saved.';
      setTimeout(() => {
        successMsg.value = '';
      }, 2500);
    } else {
      await nexceoProjectsAPI.create(payload);
      router.push(accountScopedRoute('nexceo_projects_index'));
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message ??
      e?.response?.data?.error ??
      'Failed to save project.';
  } finally {
    isSaving.value = false;
  }
}

function cancel() {
  router.push(accountScopedRoute('nexceo_projects_index'));
}

onMounted(() => {
  loadProject();
  if (isEditing.value) {
    loadSourceRules();
    loadTeamMembers();
    loadInboxes();
    loadAgents();
  }
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <ProjectWorkspaceLayout
    :project="isEditing && !isLoading ? form : null"
    :title="isEditing ? null : 'New Project'"
    :tabs="isEditing ? workspaceTabs : []"
    :active-tab-key="activeTab"
    :back-to="accountScopedRoute('nexceo_projects_index')"
    @tab-click="activeTab = $event"
  >
    <!-- Documents tab content -->
    <div
      v-if="isEditing && activeTab === 'documents'"
      class="flex-1 overflow-y-auto"
    >
      <ProjectDocuments />
    </div>

    <!-- Inventory tab content -->
    <div
      v-if="isEditing && activeTab === 'inventory'"
      class="flex-1 overflow-hidden"
    >
      <ProjectInventory />
    </div>

    <!-- Source Rules tab -->
    <div
      v-if="isEditing && activeTab === 'source_rules'"
      class="flex-1 overflow-y-auto"
    >
      <div class="flex flex-col gap-4 p-4">
        <!-- Info banner -->
        <div
          class="flex gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-400 text-sm"
        >
          <span class="i-lucide-info size-4 shrink-0 mt-0.5" />
          <div>
            <p class="font-medium">Multi-channel hint system</p>
            <p class="text-xs mt-0.5 opacity-80">
              One inbox can serve multiple projects. Rules are hints — the AI
              resolver uses them as signals, not hard assignments. Use
              <em>Default for inbox</em> only when that inbox is dedicated to
              this project.
            </p>
          </div>
        </div>

        <div
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-5 py-3 border-b border-n-weak"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
            >
              Channel / Inbox Source Rules
            </h2>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors"
              @click="showRuleForm = !showRuleForm"
            >
              <span class="i-lucide-plus size-3.5" />
              Add Rule
            </button>
          </div>

          <!-- Add rule form -->
          <div
            v-if="showRuleForm"
            class="px-5 py-4 border-b border-n-weak bg-n-alpha-1 space-y-3"
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Inbox (optional)</label
                >
                <select
                  v-model="ruleForm.inbox_id"
                  class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                >
                  <option value="">Any inbox</option>
                  <option
                    v-for="inbox in accountInboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Channel Type (optional)</label
                >
                <select
                  v-model="ruleForm.channel_type"
                  class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                >
                  <option
                    v-for="ct in CHANNEL_TYPES"
                    :key="ct.value"
                    :value="ct.value"
                  >
                    {{ ct.label }}
                  </option>
                </select>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Match Mode</label
                >
                <select
                  v-model="ruleForm.match_mode"
                  class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                >
                  <option
                    v-for="m in MATCH_MODES"
                    :key="m.value"
                    :value="m.value"
                  >
                    {{ m.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Priority (lower = first)</label
                >
                <input
                  v-model="ruleForm.priority"
                  type="number"
                  min="0"
                  max="999"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 transition-colors"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Label (optional)</label
                >
                <input
                  v-model="ruleForm.source_name"
                  type="text"
                  placeholder="e.g. WhatsApp Marketing"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                />
              </div>
            </div>
            <div class="flex items-center gap-3">
              <button
                class="px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                :disabled="ruleSaving"
                @click="saveRule"
              >
                {{ ruleSaving ? 'Saving…' : 'Save Rule' }}
              </button>
              <button
                class="px-4 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="showRuleForm = false"
              >
                Cancel
              </button>
            </div>
          </div>

          <!-- Rules list -->
          <div
            v-if="rulesLoading"
            class="flex items-center justify-center py-8"
          >
            <span
              class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7"
            />
          </div>
          <div
            v-else-if="!sourceRules.length"
            class="flex flex-col items-center justify-center py-10 text-n-slate-8"
          >
            <span class="i-lucide-radio size-8 mb-2 opacity-40" />
            <p class="text-sm">No channel rules yet</p>
          </div>
          <div v-else>
            <div
              v-for="rule in sourceRules"
              :key="rule.id"
              class="flex items-center gap-3 px-5 py-3 border-b border-n-weak last:border-0"
            >
              <div class="flex-1 min-w-0">
                <p class="text-sm text-n-slate-12 font-medium truncate">
                  {{
                    rule.source_name ||
                    rule.inbox_name ||
                    rule.channel_type ||
                    '—'
                  }}
                </p>
                <p class="text-xs text-n-slate-8 mt-0.5">
                  Mode:
                  <span class="font-medium">{{ rule.match_mode_label }}</span>
                  <template v-if="rule.inbox_name">
                    · {{ rule.inbox_name }}
                  </template>
                  <template v-if="rule.channel_type">
                    · {{ rule.channel_type }}
                  </template>
                  · Priority {{ rule.priority }}
                </p>
              </div>
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  rule.active
                    ? 'bg-success-50 dark:bg-success-800/20 text-success-700 dark:text-success-400'
                    : 'bg-n-alpha-2 text-n-slate-8'
                "
              >
                {{ rule.active ? 'Active' : 'Disabled' }}
              </span>
              <button
                class="p-1.5 rounded-lg text-n-slate-8 hover:text-ruby-500 hover:bg-ruby-50 dark:hover:bg-ruby-500/10 transition-colors"
                @click="deleteRule(rule.id)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team Members tab -->
    <div
      v-if="isEditing && activeTab === 'team_members'"
      class="flex-1 overflow-y-auto"
    >
      <div class="flex flex-col gap-4 p-4">
        <div
          class="bg-n-background border border-n-weak rounded-xl overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-5 py-3 border-b border-n-weak"
          >
            <div>
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Project Team Members
              </h2>
              <p class="text-xs text-n-slate-8 mt-0.5">
                Add project managers and executives who handle leads, visits,
                handoffs, and NEXA Staff AI support.
                <strong>Project Manager</strong> can view all project leads.
                <strong>Project Executive</strong> handles assigned leads,
                follow-ups, and site visits.
              </p>
            </div>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-woot-500 text-white text-xs font-medium hover:bg-woot-600 transition-colors"
              @click="showMemberForm = !showMemberForm"
            >
              <span class="i-lucide-user-plus size-3.5" />
              Add Member
            </button>
          </div>

          <!-- Add member form -->
          <div
            v-if="showMemberForm"
            class="px-5 py-4 border-b border-n-weak bg-n-alpha-1 space-y-3"
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Agent</label
                >
                <select
                  v-model="memberForm.user_id"
                  class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                  @change="onAgentSelected"
                >
                  <option value="">Select agent…</option>
                  <option
                    v-for="agent in accountAgents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }} ({{ agent.email }})
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Role</label
                >
                <select
                  v-model="memberForm.role"
                  class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                >
                  <option
                    v-for="r in TEAM_ROLES"
                    :key="r.value"
                    :value="r.value"
                  >
                    {{ r.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Display Name
                  <span class="font-normal text-n-slate-7"
                    >(override)</span
                  ></label
                >
                <input
                  v-model="memberForm.name"
                  type="text"
                  placeholder="Auto-filled from agent"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Email
                  <span class="font-normal text-n-slate-7"
                    >(override)</span
                  ></label
                >
                <input
                  v-model="memberForm.email"
                  type="email"
                  placeholder="Auto-filled from agent"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >WhatsApp Number
                  <span class="font-normal text-n-slate-7"
                    >(for alerts, e.g. +919876543210)</span
                  ></label
                >
                <input
                  v-model="memberForm.whatsapp_number"
                  type="tel"
                  placeholder="+91…"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Phone Number</label
                >
                <input
                  v-model="memberForm.phone_number"
                  type="tel"
                  placeholder="+91…"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Department</label
                >
                <input
                  v-model="memberForm.department"
                  type="text"
                  placeholder="e.g. Sales, Finance"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Preferred Notification</label
                >
                <select
                  v-model="memberForm.preferred_notification_channel"
                  class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                >
                  <option
                    v-for="c in NOTIFICATION_CHANNELS"
                    :key="c.value"
                    :value="c.value"
                  >
                    {{ c.label }}
                  </option>
                </select>
              </div>
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-n-slate-9 mb-1"
                  >Responsibilities
                  <span class="font-normal text-n-slate-7"
                    >(who Project AI routes to for each event)</span
                  ></label
                >
                <div class="flex flex-wrap gap-3">
                  <label
                    v-for="r in RESPONSIBILITIES"
                    :key="r.value"
                    class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer"
                  >
                    <input
                      v-model="memberForm.responsibilities"
                      type="checkbox"
                      :value="r.value"
                      class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                    />
                    {{ r.label }}
                  </label>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-4">
              <label
                class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
              >
                <input
                  v-model="memberForm.notify"
                  type="checkbox"
                  class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                />
                Send notifications to this member
              </label>
            </div>
            <div class="flex items-center gap-3">
              <button
                class="px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                :disabled="memberSaving || !memberForm.user_id"
                @click="saveMember"
              >
                {{ memberSaving ? 'Adding…' : 'Add Member' }}
              </button>
              <button
                class="px-4 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="showMemberForm = false"
              >
                Cancel
              </button>
            </div>
          </div>

          <!-- Members list -->
          <div
            v-if="membersLoading"
            class="flex items-center justify-center py-8"
          >
            <span
              class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7"
            />
          </div>
          <div
            v-else-if="!teamMembers.length"
            class="flex flex-col items-center justify-center py-10 text-n-slate-8"
          >
            <span class="i-lucide-users size-8 mb-2 opacity-40" />
            <p class="text-sm">No team members added yet</p>
          </div>
          <div v-else>
            <template v-for="member in teamMembers" :key="member.id">
              <!-- Inline edit form -->
              <div
                v-if="editingMemberId === member.id"
                class="px-5 py-4 border-b border-n-weak bg-n-alpha-1 space-y-3"
              >
                <p class="text-xs font-semibold text-n-slate-9">
                  Editing: {{ member.display_name || member.user_name }}
                </p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Role</label
                    >
                    <select
                      v-model="editForm.role"
                      class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                    >
                      <option
                        v-for="r in TEAM_ROLES"
                        :key="r.value"
                        :value="r.value"
                      >
                        {{ r.label }}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Display Name</label
                    >
                    <input
                      v-model="editForm.name"
                      type="text"
                      placeholder="Override agent name"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Email</label
                    >
                    <input
                      v-model="editForm.email"
                      type="email"
                      placeholder="Override agent email"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >WhatsApp</label
                    >
                    <input
                      v-model="editForm.whatsapp_number"
                      type="tel"
                      placeholder="+91…"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Phone Number</label
                    >
                    <input
                      v-model="editForm.phone_number"
                      type="tel"
                      placeholder="+91…"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Department</label
                    >
                    <input
                      v-model="editForm.department"
                      type="text"
                      placeholder="e.g. Sales, Finance"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Preferred Notification</label
                    >
                    <select
                      v-model="editForm.preferred_notification_channel"
                      class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                    >
                      <option
                        v-for="c in NOTIFICATION_CHANNELS"
                        :key="c.value"
                        :value="c.value"
                      >
                        {{ c.label }}
                      </option>
                    </select>
                  </div>
                  <div class="sm:col-span-2">
                    <label class="block text-xs font-medium text-n-slate-9 mb-1"
                      >Responsibilities</label
                    >
                    <div class="flex flex-wrap gap-3">
                      <label
                        v-for="r in RESPONSIBILITIES"
                        :key="r.value"
                        class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer"
                      >
                        <input
                          v-model="editForm.responsibilities"
                          type="checkbox"
                          :value="r.value"
                          class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                        />
                        {{ r.label }}
                      </label>
                    </div>
                  </div>
                </div>
                <div class="flex items-center gap-6">
                  <label
                    class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                  >
                    <input
                      v-model="editForm.notify"
                      type="checkbox"
                      class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                    />
                    Notifications
                  </label>
                  <label
                    class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
                  >
                    <input
                      v-model="editForm.active"
                      type="checkbox"
                      class="rounded border-n-weak text-woot-500 focus:ring-woot-500/20"
                    />
                    Active
                  </label>
                </div>
                <div class="flex items-center gap-3">
                  <button
                    class="px-4 py-1.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                    :disabled="editSaving"
                    @click="updateMember(member.id)"
                  >
                    {{ editSaving ? 'Saving…' : 'Save Changes' }}
                  </button>
                  <button
                    class="px-4 py-1.5 rounded-lg border border-n-weak text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                    @click="cancelEditMember"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <!-- Member row -->
              <div
                v-else
                class="flex items-center gap-3 px-5 py-3 border-b border-n-weak last:border-0"
                :class="{ 'opacity-50': !member.active }"
              >
                <div
                  class="w-8 h-8 rounded-full bg-woot-500/10 flex items-center justify-center shrink-0"
                >
                  <span class="text-xs font-semibold text-woot-600">
                    {{
                      (member.display_name ||
                        member.user_name ||
                        '?')[0].toUpperCase()
                    }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm text-n-slate-12 font-medium truncate">
                      {{ member.display_name || member.user_name }}
                    </p>
                    <span
                      v-if="!member.active"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-3 text-n-slate-8 font-medium"
                      >Inactive</span
                    >
                  </div>
                  <p class="text-xs text-n-slate-8 truncate">
                    {{ displayRoleLabel(member.role)
                    }}<template v-if="member.department">
                      · {{ member.department }} </template
                    ><template v-if="member.notify"> · Notify</template>
                  </p>
                  <div
                    v-if="member.responsibility_labels?.length"
                    class="flex flex-wrap gap-1 mt-1"
                  >
                    <span
                      v-for="label in member.responsibility_labels"
                      :key="label"
                      class="text-[10px] px-1.5 py-0.5 rounded-full bg-woot-500/10 text-woot-600 font-medium"
                    >
                      {{ label }}
                    </span>
                  </div>
                  <p
                    v-if="member.contact_email || member.whatsapp_number"
                    class="text-xs text-n-slate-7 truncate"
                  >
                    <span v-if="member.contact_email">{{
                      member.contact_email
                    }}</span>
                    <span v-if="member.contact_email && member.whatsapp_number">
                      ·
                    </span>
                    <span v-if="member.whatsapp_number">{{
                      member.whatsapp_number
                    }}</span>
                  </p>

                  <!-- Staff AI status -->
                  <div
                    v-if="member.whatsapp_number"
                    class="flex items-center gap-1.5 mt-0.5"
                  >
                    <span
                      v-if="member.staff_ai_conversation?.connected"
                      class="inline-flex items-center gap-1 text-[10px] text-success-700 bg-success-50 px-1.5 py-0.5 rounded-full"
                    >
                      <span class="i-lucide-bot size-2.5" />
                      Staff AI: Connected
                    </span>
                    <template v-else>
                      <span
                        class="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full"
                      >
                        <span class="i-lucide-bot size-2.5" />
                        Staff AI: Not set up
                      </span>
                      <button
                        :disabled="staffAiSetupLoading === member.id"
                        class="text-[10px] text-woot-600 hover:underline disabled:opacity-50"
                        @click.stop="setupStaffAiForMember(member)"
                      >
                        {{
                          staffAiSetupLoading === member.id
                            ? 'Creating…'
                            : 'Create'
                        }}
                      </button>
                    </template>
                    <a
                      v-if="member.staff_ai_conversation?.conversation_path"
                      :href="`/app/accounts/${accountId}${member.staff_ai_conversation.conversation_path.replace(/.*\/app\/accounts\/\d+/, '')}`"
                      target="_blank"
                      class="text-[10px] text-woot-500 hover:underline"
                      @click.stop
                    >
                      Open Chat
                    </a>
                  </div>

                  <!-- Setup result message -->
                  <p
                    v-if="staffAiSetupResult[member.id]"
                    class="text-[10px] mt-0.5"
                    :class="[
                      staffAiSetupResult[member.id].success
                        ? 'text-success-600'
                        : 'text-ruby-600',
                    ]"
                  >
                    {{ staffAiSetupResult[member.id].message }}
                  </p>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <button
                    class="p-1.5 rounded-lg text-n-slate-8 hover:text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
                    title="Edit member"
                    @click="startEditMember(member)"
                  >
                    <span class="i-lucide-pencil size-3.5" />
                  </button>
                  <button
                    class="p-1.5 rounded-lg text-n-slate-8 hover:text-ruby-500 hover:bg-ruby-50 dark:hover:bg-ruby-500/10 transition-colors"
                    title="Remove member"
                    @click="deleteMember(member.id)"
                  >
                    <span class="i-lucide-user-x size-3.5" />
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="(isLoading && activeTab === 'details') || (isLoading && !isEditing)"
      class="flex items-center justify-center flex-1"
    >
      <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-7" />
    </div>

    <div
      v-else-if="activeTab === 'details' || !isEditing"
      class="flex-1 overflow-y-auto"
    >
      <div class="flex flex-col gap-4 p-4">
        <!-- Error / Success -->
        <div
          v-if="error"
          class="flex items-center gap-2 p-3 rounded-lg bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/20 text-ruby-700 dark:text-ruby-400 text-sm"
        >
          <span class="i-lucide-alert-circle size-4 shrink-0" />
          {{ error }}
        </div>
        <div
          v-if="successMsg"
          class="flex items-center gap-2 p-3 rounded-lg bg-success-50 dark:bg-success-800/20 border border-success-200 dark:border-success-500/20 text-success-800 dark:text-success-400 text-sm"
        >
          <span class="i-lucide-check-circle size-4 shrink-0" />
          {{ successMsg }}
        </div>

        <!-- Two-column responsive card grid -->
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
          <!-- Card: Basic Info -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-4 flex items-center gap-1.5"
            >
              <span class="i-lucide-building-2 size-3.5 text-woot-500" />
              Basic Information
            </h2>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Project Name <span class="text-ruby-500">*</span>
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  placeholder="e.g. Sunrise Heights Phase 2"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Description
                </label>
                <textarea
                  v-model="form.description"
                  rows="3"
                  placeholder="Brief description of the project…"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 resize-none transition-colors"
                />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    Status
                  </label>
                  <div class="relative">
                    <select
                      v-model="form.status"
                      class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                    >
                      <option v-for="s in STATUSES" :key="s" :value="s">
                        {{ STATUS_LABELS[s] }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    Expected Delivery Year
                  </label>
                  <div class="relative">
                    <select
                      v-model="form.delivery_year"
                      class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                    >
                      <option :value="null">Not set</option>
                      <option
                        v-for="year in DELIVERY_YEAR_OPTIONS"
                        :key="year"
                        :value="year"
                      >
                        {{ year }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    Property Type
                  </label>
                  <div class="relative">
                    <select
                      v-model="form.property_type"
                      class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                    >
                      <option value="">Select type</option>
                      <option
                        v-for="pt in PROPERTY_TYPES"
                        :key="pt"
                        :value="pt"
                      >
                        {{ pt }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  RERA Number
                </label>
                <input
                  v-model="form.rera_number"
                  type="text"
                  placeholder="Enter project RERA registration number"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
                <p class="text-xs text-n-slate-7 mt-1">
                  AI may share this number when customers ask about project
                  registration. Legal questions still require human
                  confirmation.
                </p>
              </div>
            </div>
          </div>

          <!-- Card: Location -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-4 flex items-center gap-1.5"
            >
              <span class="i-lucide-map-pin size-3.5 text-woot-500" />
              Location
            </h2>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Address / Area
                </label>
                <input
                  v-model="form.location"
                  type="text"
                  placeholder="e.g. Plot 42, Sector 7, Bhubaneswar"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Google Maps / Location URL
                </label>
                <div class="flex gap-2">
                  <input
                    v-model="form.location_url"
                    type="url"
                    placeholder="Paste Google Maps share link, e.g. https://maps.app.goo.gl/..."
                    class="flex-1 px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                  />
                  <a
                    v-if="form.location_url"
                    :href="form.location_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-n-weak text-n-slate-12 text-sm hover:bg-n-alpha-2 transition-colors shrink-0"
                  >
                    <span class="i-lucide-map-pin size-3.5" />
                    Open Map
                  </a>
                </div>
                <p class="text-xs text-n-slate-7 mt-1">
                  AI will share this link when customers ask for location or
                  when a site visit is scheduled.
                </p>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                    >City</label
                  >
                  <input
                    v-model="form.city"
                    type="text"
                    placeholder="e.g. Bhubaneswar"
                    class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                    >State</label
                  >
                  <input
                    v-model="form.state"
                    type="text"
                    placeholder="e.g. Odisha"
                    class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                  />
                </div>
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                    >Country</label
                  >
                  <input
                    v-model="form.country"
                    type="text"
                    placeholder="India"
                    class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label class="block text-sm font-medium text-n-slate-12">
                    Coordinates
                    <span class="text-n-slate-7 font-normal"
                      >(for the Maps &amp; Surrounding tab)</span
                    >
                  </label>
                  <button
                    v-if="form.location_url"
                    type="button"
                    class="text-xs text-woot-600 hover:underline"
                    @click="extractCoordsFromUrl"
                  >
                    Extract from Maps link
                  </button>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <input
                    v-model="form.latitude"
                    type="number"
                    step="any"
                    placeholder="Latitude, e.g. 23.0479"
                    class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                  />
                  <input
                    v-model="form.longitude"
                    type="number"
                    step="any"
                    placeholder="Longitude, e.g. 72.5714"
                    class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                  />
                </div>
                <p
                  v-if="coordExtractError"
                  class="text-xs text-red-600 dark:text-red-400 mt-1"
                >
                  {{ coordExtractError }}
                </p>
              </div>
            </div>
          </div>

          <!-- Card: Pricing -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-4 flex items-center gap-1.5"
            >
              <span class="i-lucide-indian-rupee size-3.5 text-woot-500" />
              Pricing
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >Currency</label
                >
                <div class="relative">
                  <select
                    v-model="form.currency"
                    class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="AED">AED</option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >Min Price</label
                >
                <input
                  v-model="form.price_min"
                  type="number"
                  placeholder="e.g. 5000000"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >Max Price</label
                >
                <input
                  v-model="form.price_max"
                  type="number"
                  placeholder="e.g. 8500000"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 transition-colors"
                />
              </div>
            </div>
          </div>

          <!-- Card: Bedrooms -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3 flex items-center gap-1.5"
            >
              <span class="i-lucide-bed size-3.5 text-woot-500" />
              Bedroom Options
            </h2>
            <div class="flex flex-wrap gap-2 mb-3">
              <button
                v-for="b in COMMON_BEDROOMS"
                :key="b"
                class="px-3 py-1 rounded-full text-sm font-medium border transition-colors"
                :class="
                  bedrooms.includes(b)
                    ? 'bg-woot-500 text-white border-woot-500'
                    : 'bg-n-background text-n-slate-11 border-n-weak hover:border-woot-400'
                "
                @click="toggleBedroom(b)"
              >
                {{ b }}
              </button>
            </div>
            <div class="flex gap-2">
              <input
                v-model="bedroomInput"
                type="text"
                placeholder="Custom (e.g. 6BHK)"
                class="flex-1 px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 transition-colors"
                @keydown.enter.prevent="addBedroom"
              />
              <button
                class="px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-12 text-sm hover:bg-n-alpha-2 transition-colors"
                @click="addBedroom"
              >
                Add
              </button>
            </div>
            <div v-if="bedrooms.length" class="flex flex-wrap gap-2 mt-2.5">
              <span
                v-for="(b, i) in bedrooms"
                :key="i"
                class="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-n-alpha-2 text-sm text-n-slate-12"
              >
                {{ b }}
                <button
                  class="text-n-slate-8 hover:text-ruby-500 transition-colors"
                  @click="removeBedroom(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>
          </div>

          <!-- Card: Amenities -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3 flex items-center gap-1.5"
            >
              <span class="i-lucide-sparkles size-3.5 text-woot-500" />
              Amenities
            </h2>
            <div class="flex flex-wrap gap-2 mb-3">
              <button
                v-for="a in COMMON_AMENITIES"
                :key="a"
                class="px-3 py-1 rounded-full text-sm font-medium border transition-colors"
                :class="
                  amenities.includes(a)
                    ? 'bg-woot-500 text-white border-woot-500'
                    : 'bg-n-background text-n-slate-11 border-n-weak hover:border-woot-400'
                "
                @click="toggleAmenity(a)"
              >
                {{ a }}
              </button>
            </div>
            <div class="flex gap-2">
              <input
                v-model="amenityInput"
                type="text"
                placeholder="Custom amenity"
                class="flex-1 px-3 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 transition-colors"
                @keydown.enter.prevent="addAmenity"
              />
              <button
                class="px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-12 text-sm hover:bg-n-alpha-2 transition-colors"
                @click="addAmenity"
              >
                Add
              </button>
            </div>
            <div v-if="amenities.length" class="flex flex-wrap gap-2 mt-2.5">
              <span
                v-for="(a, i) in amenities"
                :key="i"
                class="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-n-alpha-2 text-sm text-n-slate-12"
              >
                {{ a }}
                <button
                  class="text-n-slate-8 hover:text-ruby-500 transition-colors"
                  @click="removeAmenity(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>
          </div>

          <!-- Card: AI Settings -->
          <div
            class="rounded-xl border border-n-weak bg-n-background p-4 flex flex-col h-full"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-4 flex items-center gap-1.5"
            >
              <span class="i-lucide-bot size-3.5 text-woot-500" />
              AI Settings
            </h2>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  AI Auto-Reply Enabled
                </p>
                <p class="text-xs text-n-slate-8 mt-0.5">
                  Allow AI employee to respond to leads for this project
                </p>
              </div>
              <button
                class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
                :class="
                  form.ai_enabled
                    ? 'bg-woot-500'
                    : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                "
                @click="form.ai_enabled = !form.ai_enabled"
              >
                <span
                  class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                  :class="form.ai_enabled ? 'translate-x-4' : 'translate-x-0.5'"
                />
              </button>
            </div>
          </div>
        </div>
        <!-- /Two-column responsive card grid -->

        <!-- Actions -->
        <div
          class="rounded-xl border border-n-weak bg-n-background p-4 flex items-center gap-3"
        >
          <button
            class="flex items-center gap-2 px-5 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            :disabled="isSaving"
            @click="submit"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            {{
              isSaving
                ? 'Saving…'
                : isEditing
                  ? 'Save Changes'
                  : 'Create Project'
            }}
          </button>
          <button
            class="px-5 py-2 rounded-lg border border-n-weak text-n-slate-12 text-sm font-medium hover:bg-n-alpha-2 transition-colors"
            @click="cancel"
          >
            Cancel
          </button>
          <div class="flex-1" />
        </div>
      </div>
    </div>
  </ProjectWorkspaceLayout>
</template>
